import { Alert, AlertDescription, AlertIcon, AlertTitle, Box, Button, Grid, GridItem, VStack } from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import { FontFamily } from '@common/theming/Fonts';
import currency from '@common/utility/currency';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { ROUTES } from 'src/apps/invest/config';
import { useCheckoutCashflowViewModel } from '../../../models/CheckoutCashflowViewModel';

type AutoSuggestionsProps = {
  onSuggestedAmount: (suggestedAmount: number) => void;
  form: UseFormReturn<{ amount: number }, any>;
};

export const AutoSuggestions: React.FC<AutoSuggestionsProps> = ({ form, onSuggestedAmount }) => {
  const viewModel = useCheckoutCashflowViewModel();
  const { suggestedAmounts, maxAmount, popularAmount } = viewModel.getSuggestedAmounts();

  return (
    <VStack width="full">
      {/* Auto Fix Max Investment Amount */}
      {form.formState.errors.amount?.type === 'max' && (
        <Alert
          status="warning"
          alignItems={'center'}
          justifyContent="center"
          marginBottom={'10px'}
          variant="left-accent">
          <AlertIcon />
          <AlertTitle>
            Based on your current Net Worth and Annual Income your maxium investment amount is ${maxAmount}. Please
            update your profile to increase your investment limit.
          </AlertTitle>
          <AlertDescription>
            <Button as={Link} to={ROUTES.profile} variant="outline" size={'sm'} colorScheme={'yellow'}>
              Update
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Suggestion Boxes */}
      <Grid width="full" templateColumns={['1fr 1fr', '1fr 1fr', '1fr 1fr', '1fr 1fr 1fr']} gap="20px">
        {suggestedAmounts.map((suggestion, i) => (
          <GridItem key={`suggestion-${i}`} colSpan={1} rowSpan={1}>
            {suggestion === popularAmount ? (
              <Button
                onClick={() => onSuggestedAmount(suggestion)}
                color="black"
                height={['40px', '40px', '40px', '80px']}
                backgroundColor={suggestion === viewModel.amount ? 'blue.50' : 'gray.50'}
                position="relative"
                _hover={{ backgroundColor: 'blue.100' }}
                width="full">
                <Box
                  display={'flex'}
                  alignItems={'center'}
                  background="linear-gradient(269.95deg, #308EFF 0.04%, #034089 150.42%)"
                  borderRadius={['10px', '5px']}
                  fontSize="12px"
                  lineHeight="22px"
                  justifyContent={'center'}
                  marginRight={['10px', '10px', '10px', '0']}
                  paddingX="10px"
                  position={['absolute']}
                  right={['-12px', '0px', '0px', 'auto']}
                  top={['-5px', '-11px', '-11px', '-11px']}
                  height={['22px', '22px', '22px', '22px']}
                  width={['22px', 'auto', 'auto', 'auto']}>
                  <TextBlock
                    weight="bold"
                    fontSize="12px"
                    color="white"
                    display={['none', 'inline', 'inline', 'inline']}>
                    Popular
                  </TextBlock>
                  <Icon display={['block', 'none', 'none', 'none']} icon="star" color={'white'} boxSize="10px" />
                </Box>
                <Box
                  fontFamily={FontFamily.HELVETICA_NOW_BOLD}
                  background="linear-gradient(270deg, #308EFF 0%, #034089 100%)"
                  backgroundClip="text">
                  {currency.format(suggestion, { maximumFractionDigits: 0 })}
                </Box>
              </Button>
            ) : (
              <Button
                onClick={() => onSuggestedAmount(suggestion)}
                color="black"
                height={['40px', '40px', '40px', '80px']}
                backgroundColor={suggestion === viewModel.amount ? 'blue.50' : 'gray.50'}
                _hover={{ backgroundColor: 'blue.100' }}
                width="full"
                fontFamily={FontFamily.HELVETICA_NOW_BOLD}
                fontWeight={'normal'}>
                {currency.format(suggestion, { maximumFractionDigits: 0 })}
              </Button>
            )}
          </GridItem>
        ))}
      </Grid>
    </VStack>
  );
};
